import { AppLayout } from "@/components/layout/AppLayout";
import { FleetModePageGate } from "@/components/tms/FleetModePageGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AccountApiKeysPage from "@/pages/account/api-keys/page";
import BillingPage from "@/pages/account/billing/page";
import SettingsPage from "@/pages/account/settings/page";
import TeamPage from "@/pages/account/team/page";
import IntegrationsPage from "@/pages/account/integrations/page";
import TwoFactorPage from "@/pages/auth/2fa/page";
import ForgotPasswordPage from "@/pages/auth/forgot-password/page";
import LoginPage from "@/pages/auth/login/page";
import ResetPasswordPage from "@/pages/auth/reset-password/page";
import SignupPage from "@/pages/auth/signup/page";
import VerifyEmailPage from "@/pages/auth/verify-email/page";
import AnalyticsPage from "@/pages/analytics/page";
import AgentPage from "@/pages/agent/page";
import CarriersPage from "@/pages/carriers/page";
import ConnectPage from "@/pages/connect/page";
import OnboardingPage from "@/pages/onboarding/page";
import QuotesPage from "@/pages/quotes/page";
import QuoteRetrievePage from "@/pages/quotes/retrieve-page";
import ShipmentBookPage from "@/pages/shipments/book-page";
import ShipmentCancelPage from "@/pages/shipments/cancel-page";
import ShipmentsPage from "@/pages/shipments/page";
import TemplatesPage from "@/pages/templates/page";
import AddressBookPage from "@/pages/addresses/page";
import ReportsPage from "@/pages/reports/page";
import TrackingPage from "@/pages/tracking/page";
import DispatchConsolePage from "@/pages/dispatch/index";
import NewLoadPage from "@/pages/dispatch/new-load";
import LoadDetailPage from "@/pages/dispatch/load-detail";
import FleetPage from "@/pages/fleet/index";
import ExceptionsPage from "@/pages/exceptions/index";
import { apiClient } from "@/lib/api/client";
import * as React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

function getPostAuthDestination(onboardingCompletedAt: string | null): "/analytics" | "/onboarding" {
  return onboardingCompletedAt ? "/analytics" : "/onboarding";
}

function RouteResolutionError({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Unable to load your workspace</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function usePostAuthDestination() {
  const [state, setState] = React.useState<{
    loading: boolean;
    destination: "/analytics" | "/onboarding" | null;
    error: string | null;
  }>({
    loading: true,
    destination: null,
    error: null,
  });

  React.useEffect(() => {
    let cancelled = false;

    apiClient.getTeamMembers()
      .then((data) => {
        if (cancelled) return;
        setState({
          loading: false,
          destination: getPostAuthDestination(data.organization.onboardingCompletedAt),
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          loading: false,
          destination: null,
          error: error instanceof Error ? error.message : "Unable to load workspace state.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

// ---------------------------------------------------------------------------
// Onboarding gate — fetches org state server-side, redirects accordingly.
// ---------------------------------------------------------------------------

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { loading, destination, error } = usePostAuthDestination();

  if (loading) return null;
  if (error) {
    return <RouteResolutionError message={error} />;
  }
  if (destination === "/analytics") {
    return <Navigate to="/analytics" replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Root redirect — respects server-side onboarding state.
// ---------------------------------------------------------------------------

function RootRedirect() {
  const { loading, destination, error } = usePostAuthDestination();

  if (loading) return null;
  if (error) {
    return <RouteResolutionError message={error} />;
  }
  if (!destination) return null;

  return <Navigate to={destination} replace />;
}

// ---------------------------------------------------------------------------
// Auth redirect — respects server-side onboarding state.
// ---------------------------------------------------------------------------

function AuthRedirect() {
  const { loading, destination, error } = usePostAuthDestination();

  if (loading) return null;
  if (error) {
    return <RouteResolutionError message={error} />;
  }
  if (!destination) return null;

  return <Navigate to={destination} replace />;
}

// ---------------------------------------------------------------------------
// Protected routes
// ---------------------------------------------------------------------------

function ProtectedRoutes({ connected }: { connected: boolean }) {
  const location = useLocation();

  if (!connected) {
    const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
    const signupPath = redirectTarget === "/"
      ? "/auth/signup"
      : `/auth/signup?redirect=${encodeURIComponent(redirectTarget)}`;
    return <Navigate to={signupPath} replace />;
  }

  return (
    <Routes>
      <Route path="/agent" element={<AgentPage />} />
      <Route
        path="/onboarding"
        element={
          <OnboardingGate>
            <OnboardingPage />
          </OnboardingGate>
        }
      />
      <Route element={<AppLayout connected={connected} />}>
        <Route path="/" element={<RootRedirect />} />
        <Route
          path="/quotes"
          element={(
            <FleetModePageGate page="quotes">
              <QuotesPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/quotes/retrieve"
          element={(
            <FleetModePageGate page="quotes">
              <QuoteRetrievePage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/shipments"
          element={(
            <FleetModePageGate page="shipments">
              <ShipmentsPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/shipments/book"
          element={(
            <FleetModePageGate page="shipments">
              <ShipmentBookPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/shipments/cancel"
          element={(
            <FleetModePageGate page="shipments">
              <ShipmentCancelPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/tracking"
          element={(
            <FleetModePageGate page="tracking">
              <TrackingPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/carriers"
          element={(
            <FleetModePageGate page="carriers">
              <CarriersPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/templates"
          element={(
            <FleetModePageGate page="templates">
              <TemplatesPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/addresses"
          element={(
            <FleetModePageGate page="addresses">
              <AddressBookPage />
            </FleetModePageGate>
          )}
        />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route
          path="/dispatch"
          element={(
            <FleetModePageGate page="dispatch">
              <DispatchConsolePage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/dispatch/loads/new"
          element={(
            <FleetModePageGate page="dispatch">
              <NewLoadPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/dispatch/loads/:id"
          element={(
            <FleetModePageGate page="dispatch">
              <LoadDetailPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/fleet"
          element={(
            <FleetModePageGate page="fleet">
              <FleetPage />
            </FleetModePageGate>
          )}
        />
        <Route
          path="/exceptions"
          element={(
            <FleetModePageGate page="exceptions">
              <ExceptionsPage />
            </FleetModePageGate>
          )}
        />
        <Route path="/account/team" element={<TeamPage />} />
        <Route path="/account/billing" element={<BillingPage />} />
        <Route path="/account/api-keys" element={<AccountApiKeysPage />} />
        <Route path="/account/settings" element={<SettingsPage />} />
        <Route
          path="/account/integrations"
          element={(
            <FleetModePageGate page="integrations">
              <IntegrationsPage />
            </FleetModePageGate>
          )}
        />
      </Route>
      <Route path="*" element={<Navigate to="/analytics" replace />} />
    </Routes>
  );
}

export function AppRouter({ connected }: { connected: boolean }) {
  return (
    <Routes>
      <Route path="/connect" element={<ConnectPage />} />
      <Route
        path="/auth/login"
        element={connected ? <AuthRedirect /> : <LoginPage />}
      />
      <Route
        path="/auth/signup"
        element={connected ? <AuthRedirect /> : <SignupPage />}
      />
      <Route
        path="/auth/forgot-password"
        element={connected ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
      />
      <Route
        path="/auth/reset-password"
        element={connected ? <Navigate to="/" replace /> : <ResetPasswordPage />}
      />
      <Route
        path="/auth/verify-email"
        element={<VerifyEmailPage />}
      />
      <Route path="/auth/2fa" element={connected ? <Navigate to="/" replace /> : <TwoFactorPage />} />
      <Route path="*" element={<ProtectedRoutes connected={connected} />} />
    </Routes>
  );
}
